import React, { useEffect, useState } from 'react';
import { FaChurch, FaEnvelope, FaMapMarkerAlt, FaPhone, FaSearch } from 'react-icons/fa';
import { useCampuses } from '../../contexts/CampusContext';
import { useCellMinistries } from '../../contexts/CellMinistryContext';
import { useLeaders } from '../../contexts/LeaderContext';

const CellGroupLocator = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCampusId, setSelectedCampusId] = useState('all');
  const { campuses } = useCampuses();
  const { leaders } = useLeaders();
  const { cellGroups } = useCellMinistries();
  const [enhancedCellGroups, setEnhancedCellGroups] = useState([]);

  // Enhance cell groups data
  useEffect(() => {
    if (cellGroups && leaders && campuses) {
      const enhanced = cellGroups.map(group => ({
        ...group,
        leader: leaders.find(l => l.cellgroupId === group.id) || null,
        campus: campuses.find(c => c.id === group.campusId) || null
      }));
      setEnhancedCellGroups(enhanced);
    }
  }, [cellGroups, leaders, campuses]);

  const filteredGroups = enhancedCellGroups.filter(group => {
    const matchesSearch = 
      group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (group.leader?.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCampus = 
      selectedCampusId === 'all' || 
      group.campusId === selectedCampusId;
    
    return matchesSearch && matchesCampus;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Discover Cell Groups
          </h1>
          <p className="mt-3 text-xl text-gray-500">
            Connect with a community near you
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-8 bg-white p-4 rounded-lg shadow-sm">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search groups or leaders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="relative w-full md:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaChurch className="h-5 w-5 text-gray-400" />
              </div>
              <select
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={selectedCampusId}
                onChange={(e) => setSelectedCampusId(e.target.value)}
              >
                <option value="all">All Campuses</option>
                {campuses?.map(campus => (
                  <option key={campus.id} value={campus.id}>{campus.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Groups List */}
        <div className="space-y-6">
          {filteredGroups.length > 0 ? (
            filteredGroups.map(group => (
              <div key={group.id} className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-6 py-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-2xl font-bold text-gray-900">{group.name}</h2>
                      <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <FaMapMarkerAlt className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                          {group.campus?.name || 'No campus assigned'}
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                          {group.meetingTime || 'Meeting time not specified'}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Leader Profile */}
                  {group.leader && (
                    <div className="mt-6 border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Group Leader</h3>
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0 h-16 w-16 rounded-full bg-gray-200 overflow-hidden">
                          {group.leader.profile_image ? (
                            <img className="h-full w-full object-cover" src={group.leader.profile_image} alt={group.leader.name} />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center text-gray-500">
                              <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{group.leader.name}</p>
                          <p className="text-sm text-gray-500 truncate">{group.leader.position || 'Cell Group Leader'}</p>
                          <div className="mt-2 flex space-x-4">
                            {group.leader.phone && (
                              <a href={`tel:${group.leader.phone}`} className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500">
                                <FaPhone className="mr-1.5 h-4 w-4" />
                                Call
                              </a>
                            )}
                            {group.leader.email && (
                              <a href={`mailto:${group.leader.email}`} className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500">
                                <FaEnvelope className="mr-1.5 h-4 w-4" />
                                Email
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Group Details */}
                  <div className="mt-6 border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Group Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Location</h4>
                        <p className="mt-1 text-sm text-gray-900">{group.location || 'Not specified'}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Meeting Day</h4>
                        <p className="mt-1 text-sm text-gray-900">{group.meetingDay || 'Not specified'}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Focus</h4>
                        <p className="mt-1 text-sm text-gray-900">{group.focus || 'General fellowship'}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Members</h4>
                        <p className="mt-1 text-sm text-gray-900">{group.memberCount || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Join This Group
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">No groups found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || selectedCampusId !== 'all' 
                  ? "Try adjusting your search or filter criteria" 
                  : "There are currently no cell groups available"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CellGroupLocator;